/**
 * Exception alerts and their delivery (KAN-43 / KAN-61 / KAN-81 / KAN-82).
 *
 * Nine modules produce findings a person ought to be told about. This file is
 * the contract that keeps one engine over all of them from becoming nine
 * bespoke checks: which alerts exist, what each one measures, what grant it
 * takes to be told, and where it drills to.
 *
 * ## The rule this file inherits
 *
 * **The alert layer never calculates a figure.** It is the same rule the
 * executive dashboard is built on (`architecture.md` §7, "the consolidated view
 * calculates nothing") and it binds harder here, because an alert is more
 * quotable than a tile: somebody is woken by it, forwards it, and acts. An
 * alert that said a matter's trust balance was $2,400 when the trust screen
 * said $2,650 would not read as a bug in the alert — it would end the firm's
 * trust in both.
 *
 * So every rule below reads a module's own output and compares it to a
 * threshold. Comparison is the only arithmetic the engine is permitted, and
 * `alert_registry.ts` is where you can check that it is the only arithmetic
 * performed. Two rules consequently have **no evaluator at all**: nothing on
 * this platform computes a cash forecast or a budget variance yet, and writing
 * those sums here would make the alert engine the firm's de facto definition of
 * them — see `moduleTicket` below and the five unavailable executive tiles for
 * the precedent.
 *
 * ## The rule this file deliberately breaks
 *
 * Billing exceptions key on a fingerprint that **includes the values that make
 * the finding true** (`architecture.md` §7), so correcting the entry stops the
 * fingerprint being produced and the finding closes by itself. That is right
 * for a queue of things to fix and would be actively harmful here.
 *
 * A trust balance drifting from $2,410 to $2,395 to $2,380 over three hourly
 * passes is *one* problem. Value-keyed fingerprints would make it three alerts,
 * three emails and three Slack messages — precisely the alert fatigue KAN-81
 * names as the thing that defeats the feature, because Finance filters the
 * channel into a folder and the one that mattered goes with it.
 *
 * So an alert's fingerprint is **rule plus the thing it is about, and never the
 * value**. The value lives in a column that is updated in place as it moves.
 * See `alertFingerprint`.
 */
/* ------------------------------------------------------------------------ *
 * The rules
 * ------------------------------------------------------------------------ */
/**
 * The eight conditions KAN-81 names, in the order Finance would triage them.
 *
 * Client money first, because a trust shortfall is a compliance exposure rather
 * than a financial one and is the only entry here that can produce a bar
 * complaint. Then cash, which is the only one that can end a firm. Then the
 * money owed and owing, then the billing hygiene rules, which are real but
 * nobody's emergency.
 */
export const ALERT_CODES = [
    'trust_low',
    'cash_shortage',
    'large_refund',
    'vendor_overdue',
    'budget_variance',
    'time_entry_incomplete',
    'rate_mismatch',
    'no_matter_assigned',
];
/**
 * How urgent, and it decides exactly one behaviour: whether quiet hours may
 * hold the message until morning.
 *
 * Three levels rather than five. Severity is only worth carrying if somebody
 * behaves differently per level, and there are two behaviours available here —
 * deliver now, or defer — plus the question of whether it belongs in a digest
 * at all. A five-point scale would be a sorting key pretending to be a
 * decision.
 *
 *  - `critical` — deliver through quiet hours. Reserved for client money and
 *    for the firm running out of cash.
 *  - `warning`  — deferred by quiet hours, delivered at the next open window.
 *  - `info`     — the same, and the natural candidate for digest mode.
 */
export const ALERT_SEVERITIES = ['critical', 'warning', 'info'];
export const ALERT_SEVERITY_META = {
    critical: {
        label: 'Critical',
        description: 'Delivered immediately, including during quiet hours.',
    },
    warning: {
        label: 'Warning',
        description: 'Delivered immediately, but held until quiet hours end.',
    },
    info: {
        label: 'Informational',
        description: 'Held by quiet hours, and the usual candidate for a digest.',
    },
};
/**
 * How the measured value is compared with the threshold.
 *
 * A closed union rather than free text because this string is read out of the
 * database and applied to money. `compareToThreshold` is the only place it is
 * interpreted, and an operator that arrived from a settings form and matched
 * nothing must be a type error here rather than a silent `false` that turns a
 * rule off without saying so.
 */
export const COMPARISON_OPERATORS = ['lt', 'lte', 'gt', 'gte'];
export const COMPARISON_OPERATOR_LABELS = {
    lt: 'is below',
    lte: 'is at or below',
    gt: 'is above',
    gte: 'is at or above',
};
/** Applies a rule's operator. The engine's only arithmetic, by design. */
export function compareToThreshold(value, operator, threshold) {
    switch (operator) {
        case 'lt':
            return value < threshold;
        case 'lte':
            return value <= threshold;
        case 'gt':
            return value > threshold;
        case 'gte':
            return value >= threshold;
    }
}
/**
 * What an alert is *about*, so the delivery layer can build a link to it and
 * the history can be grouped.
 *
 * `firm` is a real kind and not a null: a projected cash shortage is about the
 * firm rather than about any record, and modelling that as "no entity" would
 * make the deep link nullable for a case that has a perfectly good destination.
 */
export const ALERT_ENTITY_KINDS = [
    'matter',
    'client',
    'refund',
    'vendor_bill',
    'time_entry',
    'firm',
];
export const ALERT_META = {
    /**
     * $2,500 — the same figure as `DEFAULT_TRUST_FUNDING_RULE.minBalanceMinor`,
     * and deliberately the same number rather than a second opinion about the
     * same question. The trust dashboard flags a matter at that floor; an alert
     * that fired at a different one would tell Finance about matters the screen
     * called healthy.
     *
     * Critical, and the only per-record rule that is. This is client money, and
     * the cost of being told at 3am about a matter that is fine is lower than the
     * cost of being told at 9am about one that is overdrawn.
     *
     * A day's cooldown because a trust balance moves when a deposit or a
     * disbursement lands, not continuously — anything shorter is re-notifying
     * about a number nobody has had a chance to act on.
     */
    trust_low: {
        label: 'Trust balance low',
        description: "A matter's trust balance has fallen below the floor. Measured per matter and never netted against another matter's.",
        unit: 'money',
        operator: 'lt',
        defaultThreshold: 250_000,
        defaultSeverity: 'critical',
        defaultCooldownMinutes: 1_440,
        permission: 'trust_ledger.view',
        entityKind: 'matter',
        moduleTicket: 'KAN-85',
    },
    /**
     * Zero, and zero is the only defensible default for this one: the condition
     * KAN-81 names is "forecast projects negative available cash", which is a
     * statement about the sign rather than about a threshold somebody chose.
     * Finance can raise it to alert on a buffer instead.
     *
     * No evaluator. See `moduleTicket` — KAN-49 owns the forecast.
     */
    cash_shortage: {
        label: 'Projected cash shortage',
        description: 'The cash flow forecast projects available cash below the floor within its horizon.',
        unit: 'money',
        operator: 'lt',
        defaultThreshold: 0,
        defaultSeverity: 'critical',
        defaultCooldownMinutes: 1_440,
        permission: 'cash_flow.view',
        entityKind: 'firm',
        moduleTicket: 'KAN-49',
    },
    /**
     * $2,500 — `ELEVATED_APPROVAL_THRESHOLD_MINOR`, again the same number as the
     * module rather than a new one. The refund dashboard already calls a refund
     * at or above that figure `elevated` and asks for a second pair of eyes;
     * being told about exactly those is the alert, and a different boundary would
     * mean the email and the badge on the screen disagreed about which refunds
     * were material.
     *
     * Warning rather than critical: a large refund is worth knowing about the
     * same working day, and there is nothing to be done about one at 3am.
     *
     * One hour, not a day. This is a discrete event rather than a drifting
     * balance — a second large refund is genuinely a second thing to know about,
     * and the cooldown here only exists to collapse a re-evaluation of the same
     * one.
     */
    large_refund: {
        label: 'Large client refund',
        description: 'A refund at or above the elevated-approval threshold was recorded or completed.',
        unit: 'money',
        operator: 'gte',
        defaultThreshold: 250_000,
        defaultSeverity: 'warning',
        defaultCooldownMinutes: 60,
        permission: 'refunds.view',
        entityKind: 'refund',
        moduleTicket: 'KAN-58',
    },
    /**
     * 7 days past due. Vendors' own terms are usually net-30, so a bill one week
     * late has already passed the point where it was an oversight rather than a
     * decision. Alerting on day one would fire on every bill the firm intends to
     * pay on Friday.
     *
     * A day's cooldown: an overdue bill gets later every hour and none of those
     * hours is news.
     */
    vendor_overdue: {
        label: 'Vendor bill overdue',
        description: 'An unpaid vendor bill is past its due date by more than the allowed number of days.',
        unit: 'days',
        operator: 'gt',
        defaultThreshold: 7,
        defaultSeverity: 'warning',
        defaultCooldownMinutes: 1_440,
        permission: 'firm_financials.view',
        entityKind: 'vendor_bill',
        moduleTicket: 'KAN-68',
    },
    /**
     * 1,000 basis points — 10% over budget. Basis points rather than a percentage
     * for the reason `money.ts` gives for cents: a variance carried as 0.1 is a
     * float, and a threshold that has to round is a threshold that disagrees with
     * itself at the boundary.
     *
     * No evaluator. KAN-93 owns the comparison engine; `budgets` exists as a
     * table and nothing populates it.
     */
    budget_variance: {
        label: 'Budget variance',
        description: 'Actual spend has diverged from budget by more than the allowed percentage.',
        unit: 'bps',
        operator: 'gt',
        defaultThreshold: 1_000,
        defaultSeverity: 'info',
        defaultCooldownMinutes: 10_080,
        permission: 'firm_financials.view',
        entityKind: 'firm',
        moduleTicket: 'KAN-93',
    },
    /**
     * 25 open exceptions. A count rather than a per-entry alert, which is what
     * KAN-81's table asks for and is the right shape: the billing exceptions
     * queue is already a screen somebody works through, and an alert per entry
     * would be a second, worse copy of it in an inbox. What Finance needs telling
     * is that the queue has got away from them.
     *
     * Weekly cooldown, because the backlog is a standing condition rather than an
     * event and the useful cadence for "this is still bad" is the review meeting.
     */
    time_entry_incomplete: {
        label: 'Incomplete time entries',
        description: 'Open billing exceptions for missing or incomplete time entries have exceeded the allowed count.',
        unit: 'count',
        operator: 'gt',
        defaultThreshold: 25,
        defaultSeverity: 'info',
        defaultCooldownMinutes: 10_080,
        permission: 'billing_exceptions.view_all',
        entityKind: 'firm',
        moduleTicket: 'KAN-53',
    },
    /**
     * 10 open rate mismatches, and a count for the same reason as above. A rate
     * differing from the retainer is frequently a deliberate courtesy discount —
     * `BILLING_EXCEPTION_META` says so — which is exactly why one of them is not
     * worth an email and a sudden ten of them is.
     */
    rate_mismatch: {
        label: 'Billing rate discrepancies',
        description: 'Open exceptions where the rate on an entry differs from the retainer have exceeded the allowed count.',
        unit: 'count',
        operator: 'gt',
        defaultThreshold: 10,
        defaultSeverity: 'info',
        defaultCooldownMinutes: 10_080,
        permission: 'billing_exceptions.view_all',
        entityKind: 'firm',
        moduleTicket: 'KAN-53',
    },
    /**
     * Five. Lower than the other two counts because this is the one billing
     * exception that **blocks** — `no_matter_assigned` holds the money out of the
     * forecast entirely, so each one is unbillable time rather than a
     * discrepancy to confirm. A daily cooldown for the same reason: this backlog
     * costs the firm money every day it stands.
     */
    no_matter_assigned: {
        label: 'Billable time without a matter',
        description: 'Billable entries with no matter assigned have exceeded the allowed count. This time cannot be billed to anybody.',
        unit: 'count',
        operator: 'gt',
        defaultThreshold: 5,
        defaultSeverity: 'warning',
        defaultCooldownMinutes: 1_440,
        permission: 'billing_exceptions.view_all',
        entityKind: 'firm',
        moduleTicket: 'KAN-53',
    },
};
/* ------------------------------------------------------------------------ *
 * Identity: what makes two findings the same finding
 * ------------------------------------------------------------------------ */
/**
 * The key that decides whether a finding is new.
 *
 * **The tripped value is deliberately not in it.** See the header: this is the
 * one place this file departs from the billing exception convention, and it is
 * the difference between one alert about a declining trust balance and one per
 * evaluation pass forever.
 *
 * `entityRef` is the record the alert is about, or the literal `firm` for the
 * rules that are about the whole firm. Firm-level rules therefore have exactly
 * one fingerprint each, which is correct: "the exception backlog is too high"
 * is one condition that is either true or false, not one per exception in it.
 *
 * ```
 * trust_low:8f3c…             one per matter
 * no_matter_assigned:firm     one, ever
 * ```
 */
export function alertFingerprint(code, entityRef) {
    return `${code}:${entityRef}`;
}
/** The `entityRef` for a rule that is about the firm rather than a record. */
export const FIRM_ENTITY_REF = 'firm';
/* ------------------------------------------------------------------------ *
 * Lifecycle
 * ------------------------------------------------------------------------ */
/**
 * Where an alert is in its life.
 *
 * `acknowledged` earns its place by being the only way to distinguish "somebody
 * has this" from "nobody has looked at it", which is the question a Monday
 * morning triage actually asks. It does not stop the alert being auto-resolved:
 * acknowledging is a claim about attention, not about the underlying condition.
 *
 * There is no `dismissed`. Dismissing would be a person asserting the condition
 * is not real, and the condition is measured rather than asserted — the honest
 * ways for an alert to end are that it cleared, or that Finance changed the
 * threshold so it was never a condition to begin with. Both are covered.
 */
export const ALERT_STATUSES = ['open', 'acknowledged', 'resolved'];
export const ALERT_STATUS_META = {
    open: { label: 'Open', description: 'Tripped and nobody has picked it up.' },
    acknowledged: {
        label: 'Acknowledged',
        description: 'Somebody has it. The underlying condition is still true.',
    },
    resolved: {
        label: 'Resolved',
        description: 'The condition cleared, or the rule that raised it no longer applies.',
    },
};
/**
 * Why an alert ended, which the history needs and the status alone cannot say.
 *
 * The distinction that matters is `cleared` against `rule_changed`. A balance
 * that came back above the floor is the system working; an alert that vanished
 * because somebody moved the threshold below it is Finance changing the
 * question. Time-to-resolution averaged over both is a meaningless number, so
 * they are recorded apart and the history can exclude the second.
 */
export const ALERT_RESOLUTIONS = ['cleared', 'rule_changed', 'manual'];
export const ALERT_RESOLUTION_META = {
    cleared: {
        label: 'Condition cleared',
        description: 'The measured value stopped tripping the rule. Closed by the engine.',
    },
    rule_changed: {
        label: 'Rule changed',
        description: 'The rule was disabled or its threshold moved past this value. Excluded from time-to-resolution, because nothing about the underlying condition changed.',
    },
    manual: {
        label: 'Closed by hand',
        description: 'Somebody closed it without the condition clearing.',
    },
};
/** The rule as it ships, before Finance has touched it. */
export function defaultRuleConfig(code) {
    const meta = ALERT_META[code];
    return {
        code,
        /**
         * Enabled here, and the two rules with no module are switched off when the
         * API seeds them — `alert_rules.ts` reads that from the registry rather
         * than from a list, so building KAN-49 does not leave a rule disabled in a
         * file nobody thought to edit. This package cannot make that decision: it
         * describes the rules, and which of them have an evaluator is a fact about
         * the server.
         */
        enabled: true,
        threshold: meta.defaultThreshold,
        operator: meta.operator,
        severity: meta.defaultSeverity,
        recipientRoles: ['finance'],
        cooldownMinutes: meta.defaultCooldownMinutes,
        digest: false,
    };
}
/* ------------------------------------------------------------------------ *
 * Delivery (KAN-82)
 * ------------------------------------------------------------------------ */
/**
 * Where an alert can be sent.
 *
 * `in_app` is first and is not optional anywhere in this system — see
 * `NOTIFICATION_CHANNEL_META`. Email and Slack are both *copies* of a record
 * that already exists, which is what makes a failed send a delivery problem
 * rather than a lost alert.
 */
export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'slack'];
export const NOTIFICATION_CHANNEL_META = {
    in_app: {
        label: 'In-app',
        description: 'The notification centre. Always on and cannot be turned off: it is the durable record of every alert, and the reason a failed email is a delivery failure rather than a lost alert.',
        optional: false,
    },
    email: {
        label: 'Email',
        description: 'Sent from the firm’s Workspace domain. Immediate or batched into a digest, per your preference.',
        optional: true,
    },
    slack: {
        label: 'Slack',
        description: 'Posted to the finance channel. Immediate only — Slack is already a feed.',
        optional: true,
    },
};
/**
 * How often a channel batches.
 *
 * `immediate` is not a frequency and that is deliberate — it means no batching
 * at all, which is a different thing from a very short one. A digest with a
 * one-minute window would still hold an alert for a minute, and the rules that
 * are worth an interruption are worth it now.
 */
export const DIGEST_FREQUENCIES = ['immediate', 'hourly', 'daily'];
export const DIGEST_FREQUENCY_META = {
    immediate: {
        label: 'As they happen',
        description: 'One message per alert.',
        windowMinutes: null,
    },
    hourly: {
        label: 'Hourly summary',
        description: 'One message an hour listing everything raised in it.',
        windowMinutes: 60,
    },
    daily: {
        label: 'Daily summary',
        description: 'One message a day. Critical alerts still arrive immediately.',
        windowMinutes: 1_440,
    },
};
/** The firm's timezone is the reference for quiet hours. */
export const DEFAULT_QUIET_HOURS = { startMinute: 1_320, endMinute: 420 };
/**
 * Whether a local time falls inside a quiet window.
 *
 * The wrap case is the one worth reading. When `start < end` the window is an
 * ordinary interval within one day. When `start > end` it spans midnight, and
 * the test inverts: inside means at or after the start **or** before the end.
 * Written out rather than solved with modular arithmetic because this is a rule
 * somebody will check by hand against the two examples in the tests.
 */
export function isWithinQuietHours(minuteOfDay, quiet) {
    if (!quiet)
        return false;
    const { startMinute, endMinute } = quiet;
    if (startMinute === endMinute)
        return false;
    if (startMinute < endMinute) {
        return minuteOfDay >= startMinute && minuteOfDay < endMinute;
    }
    return minuteOfDay >= startMinute || minuteOfDay < endMinute;
}
/**
 * Whether quiet hours may hold this alert.
 *
 * Critical passes through, which is KAN-82's criterion stated directly. It is
 * also the reason severity is configurable per rule: the firm decides what is
 * worth a phone lighting up at 3am, and the engine only decides that whatever
 * they chose is honoured.
 */
export function deferrable(severity) {
    return severity !== 'critical';
}
export const DEFAULT_NOTIFICATION_PREFERENCES = {
    emailEnabled: true,
    /**
     * Off per person by default even though the firm has Slack enabled: a channel
     * post is the firm's decision and a direct message is the individual's. The
     * rule-level Slack post is not governed by this — see `notifications.ts`.
     */
    slackEnabled: false,
    digestFrequency: 'immediate',
    quietHours: DEFAULT_QUIET_HOURS,
    mutedCodes: [],
};
/**
 * Delivery outcome per channel, per recipient.
 *
 * `skipped` is separate from `failed` and both are separate from never having
 * been attempted. A message not sent because the recipient muted the code, or
 * because quiet hours held it, is the system working; one not sent because
 * Google returned a 500 is not. Collapsing them would make the admin's
 * failure screen unreadable within a week.
 */
export const DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'skipped'];
/**
 * Why a delivery was skipped. Recorded because "no email arrived" is a support
 * question, and the answer is almost always one of these four.
 */
export const DELIVERY_SKIP_REASONS = [
    'muted',
    'channel_disabled',
    'quiet_hours',
    'not_permitted',
    'not_configured',
];
export const DELIVERY_SKIP_REASON_LABELS = {
    muted: 'Recipient muted this alert type',
    channel_disabled: 'Recipient has this channel turned off',
    quiet_hours: 'Held by quiet hours and folded into the next window',
    not_permitted: 'Recipient does not hold the grant this alert requires',
    not_configured: 'The channel has no credentials on this deployment',
};
/* ------------------------------------------------------------------------ *
 * Presentation
 * ------------------------------------------------------------------------ */
/**
 * Where clicking an alert lands.
 *
 * KAN-81 and KAN-82 both require this and it is the difference between an alert
 * and a nuisance: being told a matter's trust is low without being taken to the
 * matter means the recipient starts a search, which is work the message could
 * have done.
 *
 * Built here rather than in the API so the in-app centre, the email and the
 * Slack post cannot drift to three different destinations, and so the SPA can
 * navigate without parsing a URL the server built for it.
 */
export function alertDeepLink(code, entityRef) {
    switch (ALERT_META[code].entityKind) {
        case 'matter':
            return `/trust-funding?matter=${encodeURIComponent(entityRef)}`;
        case 'refund':
            return `/refunds/${encodeURIComponent(entityRef)}`;
        case 'vendor_bill':
            /**
             * There is no payables screen yet (KAN-49). The executive dashboard is
             * where an AP figure is visible today, so that is where this goes — a
             * link to a page that exists beats a link to one that does not, and this
             * changes in one place when the AP screen lands.
             */
            return '/executive';
        case 'time_entry':
            return `/billing-exceptions?entry=${encodeURIComponent(entityRef)}`;
        case 'client':
            return `/refunds?client=${encodeURIComponent(entityRef)}`;
        case 'firm':
            return firmDeepLink(code);
    }
}
/** The screen a firm-level rule is really about. */
function firmDeepLink(code) {
    switch (code) {
        case 'time_entry_incomplete':
            return '/billing-exceptions?type=missing_description';
        case 'rate_mismatch':
            return '/billing-exceptions?type=rate_mismatch';
        case 'no_matter_assigned':
            return '/billing-exceptions?type=no_matter_assigned';
        default:
            return '/executive';
    }
}
/**
 * The subject line for an email.
 *
 * **The figure is deliberately not in it.** KAN-82 asks for this and the reason
 * is worth keeping written down: a subject line renders on a locked phone, on a
 * watch, and in a notification shade over somebody's shoulder on a train. "Trust
 * balance alert: Fitzgerald v Fitzgerald" is enough for the recipient to know
 * whether to open it; "$1,240 remaining" is a client's financial position
 * broadcast to a carriage.
 *
 * The value is in the body, behind an account the recipient had to sign in to.
 */
export function alertSubject(code, entityLabel) {
    const { label } = ALERT_META[code];
    return entityLabel ? `${label}: ${entityLabel}` : label;
}
/**
 * Formats a threshold for a settings form or a message body.
 *
 * Money is handed back in minor units for the caller to run through
 * `format()` — this module cannot import it without pulling currency
 * formatting into every consumer of the contract, and the two live in the same
 * package anyway.
 */
export function describeThreshold(code, threshold) {
    const meta = ALERT_META[code];
    const comparison = COMPARISON_OPERATOR_LABELS[meta.operator];
    switch (meta.unit) {
        case 'money':
            return `${comparison} ${threshold} (minor units)`;
        case 'count':
            return `${comparison} ${threshold}`;
        case 'days':
            return `${comparison} ${threshold} days`;
        case 'bps':
            return `${comparison} ${threshold} bps`;
    }
}
//# sourceMappingURL=alerts.js.map